import React, { useEffect, useState, SyntheticEvent } from 'react'
import { flowRight as compose } from 'lodash'
import { RouteChildrenProps, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Pagination, Button, Table, BulletLabel } from 'components'
import { THead } from 'components/Table'
import { IconImport, IconPlus } from 'components/Icons'
import {
  BranchList,
  BranchService,
  useBranchContext,
  withBranch,
  BREADCRUMBS_LIST,
} from 'features/Branch'
import { SearchAndFilter } from 'features/SearchAndFilter'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import { onPageChange } from 'helpers/common'
import { LIMIT_PAGE } from 'helpers/constants/common'
import {
  BRANCHTEMPLATE,
  HEADERS,
  HEADERS_LOADING,
} from 'features/Branch/constants'
import { useBreadcrumbs, useCancelToken, useSearchParams } from 'helpers/hooks'
import { formatList } from 'features/Branch/format'

import type { ISorting } from 'types'
import type { WithIParam } from 'libs/apis'
import type { IBranchSinglePropsPartial } from 'features/Branch/model'
import { OnboardingContainer } from 'containers/Onboarding'
import { downloadTemplate } from 'helpers/download'

const BranchContainer = ({ location }: RouteChildrenProps): JSX.Element => {
  const base = 'con-branch'
  const { branches, updateBranches } = useBranchContext()
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPage, setTotalPages] = useState<number>(0)
  const [showMenu, setShowMenu] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isClickBoxProduct, setIsClickBoxProduct] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams<
    WithIParam<IBranchSinglePropsPartial>
  >({
    _limit: 30,
    _page: 1,
    isActive: true,
  })
  const { t } = useTranslation(['layout', 'branch', 'common'])
  const { newCancelToken, isCancel } = useCancelToken()
  const [isOpenOnboarding, setIsOpenOnboarding] = useState(false)
  const [template, setTemplateType] = useState<string>('')

  const _page = Number(searchParams._page) || 1
  const history = useHistory()

  useBreadcrumbs(BREADCRUMBS_LIST)

  useEffect(() => {
    const getBranches = async () => {
      setLoading(true)
      setIsError(false)

      try {
        const branches = await BranchService.findAll(searchParams, {
          cancelToken: newCancelToken(),
        })
        const { body, totals } = branches
        const formattedBranch = formatList(body)

        setTotalPages(Math.ceil(Number(totals) / LIMIT_PAGE) || 1)
        updateBranches(formattedBranch, _page, searchParams)
        setLoading(false)
      } catch (e) {
        if (isCancel(e)) return

        setLoading(false)
        setIsError(true)
      }
    }

    getBranches()
  }, [location])

  const handleClickRow = (id: string) => (e: SyntheticEvent) => {
    e.preventDefault()

    if (isClickBoxProduct) {
      e.stopPropagation
    } else {
      history.push(`${routes.branch.path}/${id}/detail`)
    }
  }

  const handleToggle = () => {
    setShowMenu((prev) => !prev)
  }

  const handleSortChange = (sort: ISorting): void =>
    setSearchParams({ _sort: sort.column, _order: sort.order, _page: 1 })

  const getExcelTemplate = async () => {
    try {
      const template = await BranchService.downloadBranchTemplate()
      downloadTemplate(template, BRANCHTEMPLATE)
    } catch (e) {
      if (isCancel(e)) return
    }
  }
  return (
    <div className='container-list'>
      <SearchAndFilter
        isLoading={loading}
        placeholderSearch={t('branch:search.placeholder')}
        labelDropDown='common:status.label'
      />

      <div className='float-button--wrap'>
        <div className='float-button--group'>
          <Button
            variants='outline'
            className='buttonContent'
            withIcon={<IconImport variants='sm' />}
            onClick={() => {
              setTemplateType('branch')
              setIsOpenOnboarding(true)
            }}
          >
            {t('branch:import.button')}
          </Button>
          <Button
            className={`${base}__form-btn`}
            withIcon={<IconPlus variants='sm' />}
            onClick={() => history.push(routes.branch.create.path)}
          >
            {t('branch:create.button')}
          </Button>
        </div>
      </div>

      <div className='list-content'>
        <div className='content-bullet'>
          <BulletLabel isActive isShowLabel />
          <BulletLabel isShowLabel />
        </div>
        <div className='row'>
          <Table>
            <THead
              columns={HEADERS}
              onSortChange={handleSortChange}
              sorting={{
                column: searchParams._sort as string,
                order: searchParams._order || 'ASC',
              }}
              onToggleChange={handleToggle}
              position='left'
            />
            <BranchList
              columns={loading ? HEADERS_LOADING : HEADERS}
              branches={branches}
              showMenu={showMenu}
              loading={loading}
              isError={isError}
              onClickRow={handleClickRow}
              setIsClickBoxProduct={setIsClickBoxProduct}
            />
          </Table>
        </div>
      </div>

      <div className={`${base}__con-bottom`}>
        <Pagination
          currentPage={_page}
          totalPages={totalPage}
          onChange={(page) =>
            onPageChange(() => setSearchParams({ _page: page }))
          }
        />
      </div>

      <OnboardingContainer
        isOpen={isOpenOnboarding}
        onClose={() => setIsOpenOnboarding(false)}
        type={template}
        onDownload={() => getExcelTemplate()}
      />
    </div>
  )
}

export default compose(withBackOfficeCms, withBranch)(BranchContainer)
